import { useEffect, useRef, type RefObject } from "react";
import { NEBULA_LAYOUT, nebulaFor, paletteValues, useThemeMode, type Mode, type Rgb } from "../context/ThemeContext";
import { GRID_STEP, getGridOrigin, isMajorLine, mod, nearestLine } from "../lib/gridSnap";

// The engineering paper with the sky behind it: soft clouds in the logo colours drifting and
// fading, the 16 / 80 px grid ruled over them, and grid walkers — dots that travel only along the
// lines, joined by right-angled links, each taking the colour of the cloud it is passing through.
//
//   surface="login"  the sign-in pages: always the palette's dark theme, clouds at full strength,
//                    the grid rising out of the nebula from the bottom of the screen.
//   surface="app"    behind the shell's <main>: the grid leads, the clouds are a quiet wash. Pass the
//                    scrolling element as `scrollRef` — the grid and walkers scroll with the page,
//                    the clouds stay put — and the grid starts from the origin hooks/useGridSnap.ts
//                    lays the sheets on, so every sheet's edges sit on these lines.
//
// Sheets, the sidebar and the top bar are solid and drawn above, so nothing here sits behind text.
// Motion follows the theme's `motion` switch and prefers-reduced-motion (one still frame), runs at
// 30 frames a second and stops with the tab (requestAnimationFrame).

type Walker = { fx: number; fy: number; dx: number; dy: number; t: number; x: number; y: number; col: Rgb };

const DIRS: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const LINK = 64;
const FRAME_MS = 33;

function parseRgba(value: string): { rgb: string; a: number } {
  const m = value.match(/rgba?\(([^)]+)\)/);
  if (!m) return { rgb: "140,100,240", a: 0.1 };
  const parts = m[1].split(",").map((s) => s.trim());
  return { rgb: parts.slice(0, 3).join(","), a: parts[3] !== undefined ? parseFloat(parts[3]) : 1 };
}

export function BlueprintBackdrop({ surface, scrollRef }: {
  surface: "login" | "app";
  scrollRef?: RefObject<HTMLElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { palette, mode, motion } = useThemeMode();
  const drawMode: Mode = surface === "login" ? "dark" : mode;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const login = surface === "login";
    const colours = paletteValues(palette, drawMode);
    const nebula = nebulaFor(palette, drawMode);
    const minor = parseRgba(colours.gridMinor);
    const major = parseRgba(colours.gridMajor);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = motion && !reduced;
    const cloudAlpha = 0.5 * nebula.gain * (login ? 1 : 0.55);
    const walkerAlpha = drawMode === "dark" ? 0.9 : 0.7;
    // On the login the grid and the walkers rise out of the clouds from the bottom.
    const fade = (y: number, h: number) => (login ? 0.25 + 0.75 * Math.max(0, Math.min(1, (y / h - 0.15) / 0.6)) : 1);

    let W = 0, H = 0, t = 0;
    let walkers: Walker[] = [];
    let lastOrigin = { x: -1, y: -1 };

    const scrollTop = () => scrollRef?.current?.scrollTop ?? 0;
    const origin = () => (login ? { x: 0, y: 0 } : getGridOrigin());

    function spawn(): Walker {
      const o = origin(), s = scrollTop();
      const [dx, dy] = DIRS[Math.floor(Math.random() * 4)];
      const fx = nearestLine(Math.random() * W, o.x);
      const fy = nearestLine(s + Math.random() * H, o.y);
      return { fx, fy, dx, dy, t: Math.random() * GRID_STEP, x: fx, y: fy, col: [...nebula.ink] as Rgb };
    }

    function seed() {
      const count = Math.round((W * H) / (login ? 7000 : 11000));
      walkers = Array.from({ length: Math.max(16, Math.min(login ? 180 : 130, count)) }, spawn);
    }

    function clouds() {
      const reach = Math.max(W, H);
      return NEBULA_LAYOUT.map((l, i) => ({
        rgb: nebula.clouds[i],
        x: W * (l.px + 0.04 * Math.sin(t * 0.7 + l.phase)),
        y: H * (l.py + 0.05 * Math.cos(t * 0.5 + l.phase)),
        r: reach * l.r,
        a: 0.55 + 0.45 * Math.sin(t * 0.9 + l.phase * 1.3),
      }));
    }

    function colourAt(x: number, y: number, cs: ReturnType<typeof clouds>): Rgb {
      let w = 0, r = 0, g = 0, b = 0;
      for (const c of cs) {
        const f = Math.max(0, 1 - Math.hypot(x - c.x, y - c.y) / c.r) * c.a;
        w += f; r += c.rgb[0] * f; g += c.rgb[1] * f; b += c.rgb[2] * f;
      }
      return w < 0.05 ? nebula.ink : [r / w, g / w, b / w];
    }

    function step(advance = 0.7) {
      const o = origin(), s = scrollTop();
      if (o.x !== lastOrigin.x || o.y !== lastOrigin.y) {
        // The sheets moved the grid: put every walker back on a line.
        for (const p of walkers) { p.fx = nearestLine(p.fx, o.x); p.fy = nearestLine(p.fy, o.y); }
        lastOrigin = o;
      }
      for (let i = 0; i < walkers.length; i++) {
        const p = walkers[i];
        p.t += advance;
        if (p.t >= GRID_STEP) {
          p.t -= GRID_STEP;
          p.fx += p.dx * GRID_STEP;
          p.fy += p.dy * GRID_STEP;
          if (Math.random() < 0.35) [p.dx, p.dy] = DIRS[Math.floor(Math.random() * 4)];
          const nx = p.fx + p.dx * GRID_STEP;
          if (nx < 0 || nx > W) p.dx = -p.dx;
        }
        p.x = p.fx + p.dx * p.t;
        p.y = p.fy + p.dy * p.t;
        // Scrolled out of view: start again somewhere on screen.
        if (p.y < s - 2 * GRID_STEP || p.y > s + H + 2 * GRID_STEP) walkers[i] = spawn();
      }
    }

    function draw() {
      const c = ctx!;
      const o = origin(), s = scrollTop();
      c.fillStyle = colours.bg;
      c.fillRect(0, 0, W, H);

      const cs = clouds();
      for (const cl of cs) {
        const g = c.createRadialGradient(cl.x, cl.y, 0, cl.x, cl.y, cl.r);
        g.addColorStop(0, `rgba(${cl.rgb.join(",")},${(cloudAlpha * cl.a).toFixed(3)})`);
        g.addColorStop(1, `rgba(${cl.rgb.join(",")},0)`);
        c.fillStyle = g;
        c.fillRect(cl.x - cl.r, cl.y - cl.r, cl.r * 2, cl.r * 2);
      }

      // The grid, from the sheets' origin, scrolling with the page.
      c.lineWidth = 1;
      for (let x = mod(o.x, GRID_STEP); x <= W; x += GRID_STEP) {
        const line = isMajorLine(x, o.x) ? major : minor;
        if (login) {
          const g = c.createLinearGradient(0, 0, 0, H);
          g.addColorStop(0.15, `rgba(${line.rgb},${(line.a * 0.25).toFixed(3)})`);
          g.addColorStop(0.75, `rgba(${line.rgb},${line.a})`);
          c.strokeStyle = g;
        } else {
          c.strokeStyle = `rgba(${line.rgb},${line.a})`;
        }
        c.beginPath(); c.moveTo(Math.round(x) + 0.5, 0); c.lineTo(Math.round(x) + 0.5, H); c.stroke();
      }
      for (let yc = nearestLine(s - GRID_STEP, o.y); yc - s <= H; yc += GRID_STEP) {
        const y = yc - s;
        const line = isMajorLine(yc, o.y) ? major : minor;
        c.strokeStyle = `rgba(${line.rgb},${(line.a * fade(y, H)).toFixed(3)})`;
        c.beginPath(); c.moveTo(0, Math.round(y) + 0.5); c.lineTo(W, Math.round(y) + 0.5); c.stroke();
      }

      // Walkers take the colour of the cloud they are in, a little at a time.
      for (const p of walkers) {
        const target = colourAt(p.x, p.y - s, cs);
        p.col = [0, 1, 2].map((i) => p.col[i] + (target[i] - p.col[i]) * 0.05) as Rgb;
      }
      for (let i = 0; i < walkers.length; i++) {
        const a = walkers[i];
        for (let j = i + 1; j < walkers.length; j++) {
          const b = walkers[j];
          const d = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
          if (d >= LINK) continue;
          const ay = a.y - s, by = b.y - s;
          const alpha = (1 - d / LINK) * 0.85 * walkerAlpha * Math.min(Math.max(0.6, fade(ay, H)), Math.max(0.6, fade(by, H)));
          const g = c.createLinearGradient(a.x, ay, b.x, by);
          g.addColorStop(0, `rgba(${a.col.map(Math.round).join(",")},${alpha.toFixed(3)})`);
          g.addColorStop(1, `rgba(${b.col.map(Math.round).join(",")},${alpha.toFixed(3)})`);
          c.strokeStyle = g;
          c.beginPath(); c.moveTo(a.x, ay); c.lineTo(b.x, ay); c.lineTo(b.x, by); c.stroke();
        }
      }
      for (const p of walkers) {
        const y = p.y - s;
        c.fillStyle = `rgba(${p.col.map(Math.round).join(",")},${(walkerAlpha * Math.max(0.6, fade(y, H))).toFixed(3)})`;
        c.fillRect(p.x - 1.5, y - 1.5, 3, 3);
      }
    }

    function resize() {
      const rect = login ? { width: window.innerWidth, height: window.innerHeight } : canvas!.getBoundingClientRect();
      if (rect.width === W && rect.height === H) return;
      W = rect.width; H = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = Math.round(W * dpr);
      canvas!.height = Math.round(H * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
      draw();
    }

    resize();
    const resizeObserver = login ? null : new ResizeObserver(resize);
    resizeObserver?.observe(canvas);
    if (login) window.addEventListener("resize", resize);

    let raf = 0, last = 0, stillTimer = 0;
    const scroller = scrollRef?.current;
    const onScroll = () => { step(0); draw(); };
    if (animate) {
      const frame = (now: number) => {
        raf = requestAnimationFrame(frame);
        if (now - last < FRAME_MS) return;
        last = now;
        t += 0.024;
        step();
        draw();
      };
      raf = requestAnimationFrame(frame);
    } else {
      // Still: one frame, redrawn when the page scrolls or the sheets move the grid.
      scroller?.addEventListener("scroll", onScroll, { passive: true });
      stillTimer = window.setInterval(() => {
        const o = origin();
        if (o.x !== lastOrigin.x || o.y !== lastOrigin.y) { step(0); draw(); }
      }, 1000);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(stillTimer);
      scroller?.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
      if (login) window.removeEventListener("resize", resize);
    };
  }, [surface, palette, drawMode, motion, scrollRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: surface === "login" ? "fixed" : "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        display: "block",
      }}
    />
  );
}
